import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { ReactComponent as CloseBtn } from '../../../assets/images/graphics/btn-close.svg';

import { useMediaQuery } from 'react-responsive';

import './Object3DSlide.scss';
import { PMREMGenerator } from 'three';
import { fromEvent } from 'rxjs';
import { useSwipeEvent } from '../../../models/Hooks';

const Object3DSlide = ({ data, isActive, isExploreMode, onExploreSelected, onExploreClosed }) => {
  let [model, setModel] = useState(null);
  let [loading, setLoading] = useState(0);
  let scene = useRef(new THREE.Scene());  
  const isPortrait = useMediaQuery({ query: '(orientation: portrait)' });
  const configData = useSelector(state => state.config);
  const scale = 62;
  const renderer = useRef(null);
  const camera = useRef(null);
  const orbit = useRef(null);
  const canvasRef = useRef(null);
  let resizeTimer = null;
  const sceneRef = useRef(null);
  const menuOpened = useSelector(state => state.data.menuOpened);

  useEffect(() => {
    if(loading >= 100 && model){
      if(isExploreMode && isActive){
        console.log(model);
        animateIn();
      } else {
        animateOff();
      }
    }
  }, [isExploreMode, isActive, loading, model]);

  const animateIn = () => {
    let i = 0;
    let zoomInTimer = setInterval(() => {
      camera.current.position.z -= 0.1;
      model.rotation.y -= Math.PI / 180;
      i++;
      renderScene();
      setDefaultSceneSize();
      if (i >= 20) {
        clearInterval(zoomInTimer);
      }
    }, 25);
    renderScene();
  };

  const animateOff = () => {
    if(camera.current){
      camera.current.position.x = 0;
      camera.current.position.y = 0;
      camera.current.position.z = 8;
      camera.current.rotation.x = 0;
      camera.current.rotation.y = 0;
      camera.current.rotation.z = 0;
    }
    if(model && model.rotation && model.rotation.y){
      model.rotation.y = 0;
    }
    renderScene();
  };

  const sceneSetup = useCallback(() => {
    if (sceneRef.current && canvasRef.current && !renderer.current && isActive) {
      renderer.current = new THREE.WebGLRenderer({ antialias: true, canvas: canvasRef.current, alpha:true});
      renderer.current.setClearColor(0xffffff, 0);
      var pmrem = new PMREMGenerator(renderer.current);

      const width = sceneRef.current.clientWidth;

      if (width >= 768) {
        //size = width / 2 > 600 ? 600 : width / 2;
        camera.current = new THREE.PerspectiveCamera(
          75, // fov = field of view
          1, // aspect ratio
          0.1, // near plane
          2000 // far plane
        );
      } else {
        //size = width > 600 ? 600 : width;
        camera.current = new THREE.PerspectiveCamera(
          75, // fov = field of view
          1, // aspect ratio
          0.1, // near plane
          2000 // far plane
        );
      }

      camera.current.position.x = 0;
      camera.current.position.y = 0;
      camera.current.position.z = 8;
      sceneRef.current.appendChild(renderer.current.domElement);

      // ORBIT CONTROL
      orbit.current = new OrbitControls(camera.current, renderer.current.domElement);
      orbit.current.enablePan = false;
      orbit.current.update();

      const dirLight1 = new THREE.DirectionalLight(0xffffff, 5);
      dirLight1.position.set(0, 0, 1);
      scene.current.add(dirLight1);
      const dirLight2 = new THREE.DirectionalLight(0xffffff, 5);
      dirLight2.position.set(0, 0, -1);
      scene.current.add(dirLight2);
      //scene.current.background = null;

      setDefaultSceneSize();

      console.log('loading object...', data.Key);
      // instantiate a loader
      const loader = new GLTFLoader();

      // load a resource
      loader.load(
        // resource URL relative to the /public/index.html of the app
        // '/verizon-9-digital-museum/gltf/' + data.Object,
        configData.basePath + configData.apiMediaPath + data.Object,
        // '/verizon-9-digital-museum/gltf/new-obj.glb',
        // called when resource is loaded
        object => {
          console.log('obj: ', object);
          // dispatch(addLoadedObject({ [data.Key]: object }));
          scene.current.add(object.scene);
          object.scene.scale.set(scale, scale, scale);

          // make this element available inside of the whole component to do any animation later
          // object.scene.rotation.z = -Math.PI / 2;
          object.scene.rotation.y = 0;
          object.scene.position.x = 0;
          object.scene.position.y = 0;
          object.scene.position.z = 0;
          setModel(object.scene);
          renderScene();
        },
        // called when loading is in progress
        xhr => {
          const loadingPercentage = Math.ceil((xhr.loaded / xhr.total) * 100);
          setLoading(loadingPercentage);
          console.log(loadingPercentage + '% loaded');
        },
        // called when loading has errors
        error => {
          console.log('An error happened: ' + error);
        }
      );
    }
  }, [sceneRef, canvasRef, renderer, isActive]);

  const renderScene = useCallback(() => {
    if (renderer.current) {
      renderer.current.render(scene.current, camera.current);
    }
  }, []);

  const setDefaultSceneSize = () => {
    const width = sceneRef.current.clientWidth;
    let viewportHeight = sceneRef.current.clientHeight;
    let viewportWidth = sceneRef.current.clientWidth;
    //let viewport = Math.min(width, height);

    if (width > viewportHeight) {
      viewportWidth = width / 2;
    } else {
      viewportHeight = viewportHeight * 0.9;
    }

    canvasRef.current.width = viewportWidth;
    canvasRef.current.height = viewportHeight;
    
    camera.current.aspect = viewportWidth / viewportHeight;
    camera.current.updateProjectionMatrix();
    renderer.current.setSize(viewportWidth, viewportHeight);
    renderScene();

  };

  const handleWindowResize = useCallback(() => {

    if (resizeTimer) {
      clearTimeout(resizeTimer);
    }
    resizeTimer = setTimeout(() => {
      setDefaultSceneSize();
    }, 50);
  }, [sceneRef]);

  useEffect(() => {
    if (canvasRef.current && isActive && !renderer.current) {
      sceneSetup();
      renderScene();

      window.addEventListener('resize', handleWindowResize);
      orbit.current.addEventListener('change', renderScene);
      canvasRef.current.addEventListener("touchstart", handleCanvasEvent);
      canvasRef.current.addEventListener("touchend", handleCanvasEvent);
      canvasRef.current.addEventListener("touchcancel", handleCanvasEvent);
      
    }
    return () => {
      // window.removeEventListener('resize', handleWindowResize);
      // orbit.current && orbit.current.removeEventListener('change', renderScene);
      // if (model && model.dispose) model.dispose();
    };
  }, [sceneSetup, renderScene, handleWindowResize, model]);

  useEffect(() => {

    if (!isExploreMode && camera.current && model) {
      setDefaultSceneSize();
      animateOff();
    }
  }, [isExploreMode, model, loading]);

  useEffect(() => {
    if (!isActive && camera.current && model) {
      setDefaultSceneSize();
      animateOff();
    }
  }, [isActive, model]);

  const handleCanvasEvent = (e) =>{
    e.stopPropagation();
    e.preventDefault();
  }

  const handleExploreSelected = (e) => {
    e.stopPropagation();
    e.preventDefault();
    onExploreSelected();
  };

  const handleExploreClosed = (e) => {
    e.stopPropagation();
    e.preventDefault();
    onExploreClosed();
  };

  return (
    <div className={`object-3D-slide ${isExploreMode ? 'explore-mode' : ''}`}>
      <div className="scene-ref" ref={sceneRef}>
        <canvas ref={canvasRef} alt={data.AltText3D} tabIndex={isActive && !menuOpened ? 0 : -1}></canvas>
        {loading < 100 ? <div className="loading">Loading 3D model... {loading}%</div> : null}
      </div>
      <div className="cover"></div>
      {loading >= 100 && !isExploreMode ? <button className="explore" onClick={handleExploreSelected}>Explore 360</button> : null}
      {loading >= 100 && !isPortrait && isExploreMode ? <button className="close" onClick={handleExploreClosed}><CloseBtn /></button> : null}
    </div>
  );
};

export default Object3DSlide;
